import { useState, useEffect, useCallback, useMemo } from 'react'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import IconButton from '@mui/material/IconButton'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Divider from '@mui/material/Divider'
import Link from '@mui/material/Link'
import RefreshIcon from '@mui/icons-material/Refresh'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Refreshing from 'components/core/Refreshing'
import {
  FlowLayout,
  accountFrom,
  accountParams,
  useScrollToHash,
  withAccount,
} from '../Flow/parts'
import { usePipelineReads } from '../Flow/reads'
import PipelinePanel from '../Flow/PipelinePanel'
import StackedDayChart from './StackedDayChart'
import LagChart from './LagChart'
import DayTable from './DayTable'
import UserFilter from './UserFilter'
import StratumFilter from './StratumFilter'
import SpanTable from './SpanTable'
import RouterHeard from './RouterHeard'
import FleetSpans from './FleetSpans'
import {
  LAGS,
  PRODUCT_LAGS,
  OUTCOMES,
  REASONS,
  PERCENTILES,
  COVERAGE,
  CONTINUITY,
  CAPTURE_TILES,
  fillHorizon,
  causeSeries,
  causeBucket,
  fmtSeconds,
  fmtHours,
  fmtCount,
  fmtCoverage,
  fmtRate,
  fmtKbps,
  kbpsTone,
  coverageTone,
  durableByClient,
  reasonCounts,
  WINDOWS,
} from './vocabulary'

// `id` is the section's anchor, which the flow's stages link to.
const SectionHeading = ({ id, title, help }) => (
  <Box
    id={id}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      mb: 1.5,
      scrollMarginTop: 24,
    }}
  >
    <Typography variant="subtitle1" fontWeight={700}>
      {title}
    </Typography>
    <Tooltip title={help} placement="right">
      <InfoOutlinedIcon
        sx={{ fontSize: 16, color: 'text.secondary', cursor: 'default' }}
      />
    </Tooltip>
  </Box>
)

// The window totals, above the charts on purpose. On a healthy fleet a
// handful of lost spans is a sub-pixel sliver in a stacked bar of thousands,
// and these are where a single-digit loss stays legible.
const Tile = ({ label, value, help, tone }) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, flex: '1 1 150px' }}>
    <Tooltip title={help} placement="top">
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ cursor: 'default' }}
      >
        {label}
      </Typography>
    </Tooltip>
    <Typography
      variant="h5"
      fontWeight={700}
      color={tone || 'text.primary'}
      sx={{ mt: 0.5 }}
    >
      {value}
    </Typography>
  </Paper>
)

// Step 2b's rows (the pipeline fold's `transcription` block): mechanism for
// staff, never a product number (invariant 14). Allowance, spend, concurrency
// and the credit buckets are fleet-only; a user filter shows that account's
// own transcribed, skipped and backlog numbers.
const TranscriptionHealth = ({ asr, fleet }) => {
  const backlog = asr.backlog || {}
  // The alarm reads every untranscribed span, whatever it waits on.
  const untranscribed = asr.untranscribed || backlog
  const unknown = Boolean(backlog.timed_out || untranscribed.timed_out)
  const pastAlarm =
    asr.alarm_age_h != null &&
    (untranscribed.oldest_age_h || 0) > asr.alarm_age_h
  const queue = asr.queue || null
  const skipped = Object.entries(asr.skipped_hours_by_reason || {})
  const allowance = asr.allowance
  const spend = asr.provider_spend
  const rejected = asr.rejected_runs || {}
  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
      <SectionHeading
        id="transcription"
        title="Transcription"
        help="Speech the router kept, sent to ElevenLabs to be transcribed. What the budget won't cover is skipped, not queued for later."
      />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Tile
          label="Transcribed"
          value={fmtHours(asr.transcribed_hours)}
          help={`${fmtHours(
            asr.transcribed_premium_hours
          )} premium (Scribe) and ${fmtHours(
            asr.transcribed_bulk_hours
          )} bulk over the window.`}
        />
        <Tile
          label="Backlog · waiting"
          value={
            unknown
              ? 'unknown'
              : `${fmtCount(backlog.spans || 0)} · ${
                  untranscribed.oldest_age_h != null
                    ? fmtHours(untranscribed.oldest_age_h)
                    : '—'
                }`
          }
          tone={
            pastAlarm
              ? 'error.main'
              : unknown || backlog.spans
              ? 'warning.main'
              : undefined
          }
          help={`Spans waiting to be transcribed, and the oldest one's age (${fmtCount(
            untranscribed.spans || 0
          )} untranscribed in all). Red past ${fmtHours(
            asr.alarm_age_h
          )}: wait too long and its speaker turns are lost. "unknown": the read timed out.`}
        />
        <Tile
          label="Provider unavailable"
          value={fmtCount(asr.unavailable?.spans || 0)}
          tone={asr.unavailable?.spans ? 'warning.main' : undefined}
          help="Spans waiting out an ElevenLabs outage. Retried for up to a day."
        />
        <Tile
          label="Unanswered, retrying"
          value={`${fmtCount(asr.unanswered?.spans || 0)}${
            asr.unanswered?.oldest_age_h != null
              ? ` · ${fmtHours(asr.unanswered.oldest_age_h)}`
              : ''
          }`}
          tone={asr.unanswered?.spans ? 'warning.main' : undefined}
          help="Requests ElevenLabs never answered, and the oldest one's age. Retried in halves for up to a day."
        />
        <Tile
          label="Skipped"
          value={fmtHours(asr.skipped_hours)}
          tone={asr.skipped_hours > 0 ? 'warning.main' : undefined}
          help={
            skipped.length
              ? skipped
                  .map(([reason, row]) => `${reason}: ${fmtHours(row.hours)}`)
                  .join(' · ')
              : 'Nothing skipped: the budget covered every run.'
          }
        />
        <Tile
          label={`Credits would skip · ${asr.credit_mode}`}
          value={fmtHours(asr.would_skip_hours)}
          help="What the per-person budget would skip if it were on. Nothing is skipped."
        />
        <Tile
          label="Sent, never answered"
          value={fmtHours(asr.unanswered_hours)}
          help="Audio sent that got no answer. Counted as spent, to see whether ElevenLabs bills it."
        />
        <Tile
          label="Rejected runs · last hour / window"
          value={`${fmtCount(rejected.last_hour || 0)} / ${fmtCount(
            rejected.window || 0
          )}`}
          tone={rejected.alerting ? 'error.main' : undefined}
          help={`Runs ElevenLabs refused as a file. Red over ${
            rejected.alert_at ?? 5
          } an hour: usually our bug.`}
        />
        {fleet && allowance && (
          <Tile
            label={`Allowance today · ${allowance.mode || 'enforcing'}${
              allowance.reconciled ? '' : ' · unreconciled'
            }`}
            value={
              allowance.today_hours != null
                ? `${fmtHours(allowance.remaining_today_hours)} / ${fmtHours(
                    allowance.today_hours
                  )}`
                : '—'
            }
            tone={
              allowance.over_pool
                ? 'error.main'
                : allowance.reconciled
                ? undefined
                : 'warning.main'
            }
            help={`Today's share of the ElevenLabs credits, and what's left. ${
              allowance.mode === 'shadow'
                ? `Measuring only: ${fmtHours(
                    asr.allowance_would_skip_hours || 0
                  )} would have been skipped this window. `
                : ''
            }${
              allowance.over_pool ? "Past this cycle's included credits. " : ''
            }${
              allowance.reported_at
                ? 'Checked against ElevenLabs.'
                : 'Not checked against ElevenLabs yet.'
            } Resets ${allowance.cycle_reset}.`}
          />
        )}
        {fleet && spend && (
          <Tile
            label="Provider spend"
            value={`$${spend.usd.toFixed(2)}`}
            help={`What we sent, at list price (${
              spend.provenance.join(', ') || '—'
            })${
              spend.unpriced.length
                ? `; unpriced: ${spend.unpriced.join(', ')}`
                : ''
            }.`}
          />
        )}
        {fleet && asr.concurrency?.peak_workspace != null && (
          <Tile
            label="ElevenLabs concurrency · workspace peak"
            value={`${fmtCount(asr.concurrency.peak_workspace)}${
              asr.concurrency.maximum != null
                ? ` / ${fmtCount(asr.concurrency.maximum)}`
                : ''
            }`}
            help={`Most requests running at once today, across every environment on our ElevenLabs key. This lane's own peak: ${fmtCount(
              asr.concurrency.peak_current || 0
            )}.`}
          />
        )}
        {asr.latency?.runs > 0 && (
          <Tile
            label="Scribe answer time · p95"
            value={`${asr.latency.s_per_audio_s_p95} s/audio-s`}
            tone={asr.latency.over_ceiling > 0 ? 'warning.main' : undefined}
            help={`Seconds ElevenLabs takes per second of audio, over ${fmtCount(
              asr.latency.runs
            )} runs (p50 ${asr.latency.s_per_audio_s_p50}). ${fmtCount(
              asr.latency.over_ceiling
            )} ran past the ${asr.latency.ceiling_s} s limit.`}
          />
        )}
        {fleet && queue && (
          <Tile
            label="Queue · stale writes"
            value={`${fmtCount(queue.stale_writes || 0)}${
              queue.last_stale_age_h != null
                ? ` · ${fmtHours(queue.last_stale_age_h)} ago`
                : ''
            }`}
            tone={
              queue.last_stale_age_h != null && queue.last_stale_age_h < 1
                ? 'error.main'
                : undefined
            }
            help={`Writes refused because another worker had taken the job over, and how long ago the last was. Red within the hour. Taken over: ${fmtCount(
              queue.leases_reclaimed || 0
            )}. Our failures awaiting retry: ${fmtCount(
              asr.failed_pending?.spans || 0
            )}.`}
          />
        )}
        {fleet && asr.credits_distribution?.timed_out && (
          <Tile
            label="Credit buckets · p50"
            value="unknown"
            tone="warning.main"
            help="The balance read timed out: unknown, never zero."
          />
        )}
        {fleet && asr.credits_distribution?.users > 0 && (
          <Tile
            label="Credit buckets · p50"
            value={fmtHours(asr.credits_distribution.p50_hours)}
            help={`${fmtCount(
              asr.credits_distribution.users
            )} users; p10 ${fmtHours(
              asr.credits_distribution.p10_hours
            )}, p90 ${fmtHours(asr.credits_distribution.p90_hours)}; ${fmtCount(
              asr.credits_distribution.empty
            )} empty.`}
          />
        )}
        <Tile
          label="Waiting for selection"
          value={fmtCount(asr.selection_backlog?.spans || 0)}
          help="Transcribed spans not yet read for quotes."
        />
      </Box>
    </Paper>
  )
}

const legendOf = (entries, counts, horizonHours) =>
  entries.map(({ key, label, color, help }) => ({
    key,
    label,
    color,
    help: fillHorizon(help, horizonHours),
    count: counts[key] || 0,
  }))

// The page's only legend, and the charts have none of their own: ECharts' can
// toggle a series but carries neither a description nor a count, and two
// legends for one chart is two things to keep in step. This carries all three
// — hover for what a series means and to emphasise it in the chart, the
// window's count beside it, and a click that isolates it.
//
// Click isolates rather than excludes, and clicking the isolated one restores
// everyone: the same semantics as the Heartbeats charts, and the one you want
// far more often — "show me only jetsam" beats "show me everything but jetsam".
const Legend = ({ items, focused, onFocus, onHover }) => (
  <Box
    sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}
    onMouseLeave={() => onHover?.(null)}
  >
    {items.map(({ key, label, color, help, count }) => {
      const dimmed = focused != null && focused !== key
      return (
        <Tooltip key={key} title={help} placement="top">
          <Box
            onClick={() => onFocus?.(key)}
            onMouseEnter={() => onHover?.(key)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              cursor: onFocus ? 'pointer' : 'default',
              opacity: dimmed ? 0.35 : 1,
              userSelect: 'none',
              transition: 'opacity 120ms',
            }}
          >
            <Box
              sx={{
                width: 9,
                height: 9,
                borderRadius: '50%',
                bgcolor: color,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
            {count != null && (
              <Typography variant="caption" fontWeight={700}>
                {fmtCount(count)}
              </Typography>
            )}
          </Box>
        </Tooltip>
      )
    })}
  </Box>
)

// The Pipeline page: audio in to quotes out, in full — durability, each
// transport hop, routing, transcription, selection and capture health
// (docs/audio-streaming/step-1-ingestion.md, "Latency instrumentation"). The
// Overview page draws the same reads as one flow, with its sentence and the
// product's number; this is what that flow is computed from.
const StreamIngest = () => {
  // A week: `1d` is today so far in UTC, which on an evening in the US is a
  // few hours old and can read as nothing having streamed at all.
  const [windowDays, setWindowDays] = useState(7)
  // A link names an account (`user`, with `name` for the filter's label),
  // and one from the Moments page (`routerHeardPath`) one of its spans too
  // (`span`): the page opens on that account, with "What the router heard"
  // starting at that span. Picking another account, or "Show newest", drops
  // the span; the URL keeps whichever account is picked, so the Overview link
  // and a reload stay on it.
  const [searchParams, setSearchParams] = useSearchParams()
  const [user, setUser] = useState(() => accountFrom(searchParams))
  const [focusSpan, setFocusSpan] = useState(() => {
    const startMs = Number(searchParams.get('span'))
    return Number.isFinite(startMs) && startMs > 0 ? startMs : null
  })
  const pickUser = (next) => {
    setUser(next)
    setFocusSpan(null)
    setSearchParams(accountParams(next), { replace: true })
  }
  const clearFocus = () => {
    setFocusSpan(null)
    setSearchParams(accountParams(user), { replace: true })
  }
  const [percentile, setPercentile] = useState('p90_s')
  const [sealCause, setSealCause] = useState(null)
  const [clientVersion, setClientVersion] = useState(null)
  const [causeView, setCauseView] = useState('volume')
  // Which series a legend has isolated, namespaced per chart so the four
  // legends do not share a selection; and which one is hovered, which only
  // ever applies to a single legend at a time.
  const [focused, setFocused] = useState({})
  const [hovered, setHovered] = useState(null)
  const [selectedDay, setSelectedDay] = useState(null)
  // The pipeline's reads, once, for the panel heading the page and every
  // section under it (`Flow/reads.js`).
  const reads = usePipelineReads({
    days: windowDays,
    userId: user?.id || null,
    sealCause,
    clientVersion,
  })
  const { payload, capture, pipeline, totals, loading, error } = reads
  // A stage's link names a section; it is drawn once the reads land.
  useScrollToHash(Boolean(payload && capture))

  const days = useMemo(() => payload?.days || [], [payload])

  // The newest day, until a bar or row is clicked. Re-pinned when the window
  // or the user changes takes that day out of the response.
  useEffect(() => {
    if (!days.length) {
      setSelectedDay(null)
      return
    }
    setSelectedDay((current) =>
      current && days.some(({ day }) => day === current) ? current : days[0].day
    )
  }, [days])

  const durablePct =
    totals.declared > 0
      ? `${((totals.durable / totals.declared) * 100).toFixed(2)}%`
      : '—'

  const outcomeSeries = useMemo(
    () =>
      OUTCOMES.map((outcome) => ({
        ...outcome,
        valueOf: (row) =>
          outcome.key === 'durable_by_client'
            ? durableByClient(row.counts)
            : row.counts[outcome.key],
      })),
    []
  )

  const reasonSeries = useMemo(
    () =>
      REASONS.map((reason) => ({
        ...reason,
        valueOf: (row) => reasonCounts(row.counts)[reason.key] || 0,
      })),
    []
  )

  // Step 4a's product half: its own day rows (`product.days`), so its own
  // series rather than LAGS over `transport`.
  const productDays = useMemo(() => payload?.product?.days || [], [payload])
  const productSeries = useMemo(
    () =>
      PRODUCT_LAGS.map((lag) => ({
        ...lag,
        valueOf: (row, p) => row[lag.key]?.[p] ?? null,
      })),
    []
  )
  const productTotals = useMemo(() => {
    const byLag = {}
    PRODUCT_LAGS.forEach(({ key }) => {
      byLag[key] = productDays.reduce((sum, row) => sum + (row[key]?.n || 0), 0)
    })
    return byLag
  }, [productDays])

  const hopSeries = useMemo(
    () =>
      LAGS.map((lag) => ({
        ...lag,
        valueOf: (row, p) => row.transport[lag.key][p],
      })),
    []
  )

  // Series come from the window's own causes, so a cause this page has never
  // heard of charts without a release.
  const causes = useMemo(() => causeSeries(payload?.facets), [payload])

  const causeVolumeSeries = useMemo(
    () =>
      causes.map((entry) => ({
        ...entry,
        valueOf: (row) => causeBucket(row, entry.cause)?.declared || 0,
      })),
    [causes]
  )

  const causeLagSeries = useMemo(
    () =>
      causes.map((entry) => ({
        ...entry,
        valueOf: (row, p) =>
          causeBucket(row, entry.cause)?.end_to_sealed?.[p] ?? null,
      })),
    [causes]
  )

  // Capture days are the same UTC days as the latency rows (both bucket on
  // `declared_at`), so the selected day indexes both and the two sections
  // always describe the same population.
  const captureDays = useMemo(() => capture?.days || [], [capture])
  const selectedCapture = useMemo(
    () => captureDays.find(({ day }) => day === selectedDay) || null,
    [captureDays, selectedDay]
  )

  const coverageSeries = useMemo(
    () => [
      ...COVERAGE.map((entry) => ({
        ...entry,
        valueOf: (row) => row.coverage[entry.key],
      })),
      // On the same axes on purpose: the vertical distance between continuity
      // and coverage is the audio that was recorded and never arrived, and it
      // is only readable when the two are drawn together.
      ...CONTINUITY.map((entry) => ({
        ...entry,
        key: `continuity_${entry.key}`,
        valueOf: (row) => row.continuity?.[entry.key] ?? null,
      })),
    ],
    []
  )

  // Gap hours and audio hours sum over the window; every other capture number
  // is a distribution and summing one would be meaningless — there is no such
  // thing as a total median, and the tiles show the selected day for those.
  const captureTotals = useMemo(
    () =>
      captureDays.reduce(
        (sum, row) => ({
          gap_hours: Math.round((sum.gap_hours + row.gaps.hours) * 100) / 100,
          audio_hours:
            Math.round((sum.audio_hours + row.audio_hours) * 100) / 100,
          declared_hours:
            Math.round((sum.declared_hours + row.declared_hours) * 100) / 100,
        }),
        { gap_hours: 0, audio_hours: 0, declared_hours: 0 }
      ),
    [captureDays]
  )

  const focusedIn = useCallback((chart) => focused[chart] ?? null, [focused])
  const focusOn = useCallback(
    (chart) => (key) =>
      setFocused((prev) => ({
        ...prev,
        // Clicking the isolated series restores everyone.
        [chart]: prev[chart] === key ? null : key,
      })),
    []
  )
  const hoverIn = useCallback(
    (chart) => (key) => setHovered(key == null ? null : { chart, key }),
    []
  )
  const highlightIn = useCallback(
    (chart) => (hovered?.chart === chart ? hovered.key : null),
    [hovered]
  )
  const visible = useCallback(
    (chart, entries) => {
      const only = focusedIn(chart)
      return only == null ? entries : entries.filter(({ key }) => key === only)
    },
    [focusedIn]
  )

  // Legend counts: spans per cause over the window (the facet total), and for
  // the hops the size of each lag's sample, which is what says whether a
  // percentile over it is worth trusting.
  const causeTotals = useMemo(() => {
    const byCause = {}
    causes.forEach(({ key, cause }) => {
      const facet = (payload?.facets?.seal_cause || []).find(
        (entry) => entry.value === cause
      )
      byCause[key] = facet ? facet.n : 0
    })
    return byCause
  }, [causes, payload])

  const hopTotals = useMemo(() => {
    const byLag = {}
    LAGS.forEach(({ key }) => {
      byLag[key] = days.reduce((sum, row) => sum + row.transport[key].n, 0)
    })
    return byLag
  }, [days])

  const selected = days.find(({ day }) => day === selectedDay)
  const percentileLabel = PERCENTILES.find(
    ({ key }) => key === percentile
  ).label
  // The server reports its own abandon cutoff, so the tooltips state that
  // rather than a number baked in here. Older deploys omit it, and the
  // wording falls back to naming the horizon without a figure.
  const horizonHours = payload?.horizon_hours ?? null
  const facets = payload?.facets ?? {}
  // A stratum narrows every figure on the page, so say so once rather
  // than qualifying each tile.
  const stratum = [sealCause, clientVersion].filter(Boolean)

  return (
    <Box sx={{ px: 6, py: 4, width: '100%', maxWidth: 1600, mx: 'auto' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 1,
        }}
      >
        <Typography variant="h5" fontWeight={700}>
          Pipeline
        </Typography>
        {loading && <CircularProgress size={18} />}
        <Box sx={{ flex: 1 }} />
        <UserFilter user={user} onChange={pickUser} />
        <StratumFilter
          label="Seal cause"
          value={sealCause}
          facets={facets.seal_cause}
          onChange={setSealCause}
        />
        <StratumFilter
          label="Client build"
          value={clientVersion}
          facets={facets.client_version}
          onChange={setClientVersion}
          width={210}
        />
        <ToggleButtonGroup
          value={windowDays}
          exclusive
          size="small"
          onChange={(event, next) => next && setWindowDays(next)}
        >
          {WINDOWS.map(({ days: value, label, title }) => (
            <ToggleButton key={value} value={value} title={title}>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={reads.refresh}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Audio in → quotes out, per UTC day
        {user ? ` · ${user.username || user.id}` : ' · fleet'}
        {stratum.length ? ` · ${stratum.join(' · ')}` : ''}
        {payload?.as_of
          ? ` · as of ${dayjs(payload.as_of).format('HH:mm:ss')} (cached 60s)`
          : ''}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        The pipeline’s row of the{' '}
        <Link component={RouterLink} to={withAccount('/overview', user)}>
          Overview
        </Link>
        , then everything it is computed from: the durability breakdown, why
        bundles were lost, how they ended, each hop as a distribution, routing,
        transcription and capture health. One account’s own rows — yours, unless
        the filter names someone — sit under their own heading at the end.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Faded while a window, filter or refresh is read: what the window
          decides, which ends before the fleet's span list. */}
      <Refreshing active={loading}>
        <FlowLayout>
          <PipelinePanel
            reads={reads}
            user={user}
            stratified={Boolean(sealCause || clientVersion)}
            to={null}
          />
        </FlowLayout>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Tile
            label="Spans declared"
            value={fmtCount(totals.declared)}
            help="Spans the phones told us about in this window."
          />
          <Tile
            label="Durable"
            value={durablePct}
            help="Share of spans that arrived in full."
          />
          <Tile
            label="Never durable"
            value={fmtCount(totals.never_durable)}
            tone={totals.never_durable > 0 ? 'error.main' : undefined}
            help="Spans not safe yet. Some are still uploading, not lost: see the breakdown below."
          />
          <Tile
            label="Unsealed audio"
            value={fmtHours(totals.unsealed_hours)}
            tone={totals.unsealed_hours > 0 ? 'error.main' : undefined}
            help="Declared audio on spans with no seal stamp. The loss counter."
          />
          <Tile
            label={`End → durable ${percentileLabel}`}
            value={fmtSeconds(
              selected ? selected.transport.end_to_sealed[percentile] : null
            )}
            help="Audio ended to bytes verified, on the selected day."
          />
        </Box>

        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
          <SectionHeading
            id="durability"
            title="Durability"
            help="Every span declared each day, by how it ended. Red never arrived in full. Click a day to pin it."
          />
          <Legend
            items={legendOf(OUTCOMES, totals, horizonHours)}
            focused={focusedIn('durability')}
            onFocus={focusOn('durability')}
            onHover={hoverIn('durability')}
          />
          <StackedDayChart
            days={days}
            series={visible('durability', outcomeSeries)}
            highlighted={highlightIn('durability')}
            selectedDay={selectedDay}
            onSelectDay={setSelectedDay}
          />
        </Paper>

        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
          <SectionHeading
            id="never-durable"
            title="Why spans never became durable"
            help="Spans that didn't arrive in full, by why. On their own scale so small counts show."
          />
          <Legend
            items={legendOf(REASONS, totals, horizonHours)}
            focused={focusedIn('reasons')}
            onFocus={focusOn('reasons')}
            onHover={hoverIn('reasons')}
          />
          <StackedDayChart
            days={days}
            series={visible('reasons', reasonSeries)}
            highlighted={highlightIn('reasons')}
            height={200}
            selectedDay={selectedDay}
            onSelectDay={setSelectedDay}
          />
          {selected && selected.counts.never_durable > 0 && (
            <Typography variant="caption" color="text.secondary">
              {selected.day}:{' '}
              {REASONS.filter(({ key }) => reasonCounts(selected.counts)[key])
                .map(
                  ({ key, label }) =>
                    `${
                      reasonCounts(selected.counts)[key]
                    } ${label.toLowerCase()}`
                )
                .join(' · ')}{' '}
              · {fmtHours(selected.counts.unsealed_hours)} unsealed
            </Typography>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <SectionHeading
              id="how-spans-end"
              title="How spans end"
              help="How each span ended, as the phone reported it: full length, a stop, a call, the app dying, a quote or another app playing for over 2 minutes. A growing share of interruptions means recordings are being cut short."
            />
            <ToggleButtonGroup
              value={causeView}
              exclusive
              size="small"
              sx={{ mb: 1.5 }}
              onChange={(event, next) => next && setCauseView(next)}
            >
              <ToggleButton value="volume">Volume</ToggleButton>
              <ToggleButton value="latency">End &rarr; durable</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {causes.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No spans in this window report a seal cause yet.
            </Typography>
          ) : (
            <>
              <Legend
                items={causes.map((entry) => ({
                  ...entry,
                  count: causeTotals[entry.key] || 0,
                }))}
                focused={focusedIn('causes')}
                onFocus={focusOn('causes')}
                onHover={hoverIn('causes')}
              />
              {causeView === 'volume' ? (
                <StackedDayChart
                  days={days}
                  series={visible('causes', causeVolumeSeries)}
                  highlighted={highlightIn('causes')}
                  height={220}
                  selectedDay={selectedDay}
                  onSelectDay={setSelectedDay}
                />
              ) : (
                <LagChart
                  days={days}
                  series={visible('causes', causeLagSeries)}
                  highlighted={highlightIn('causes')}
                  percentile={percentile}
                  height={220}
                  sampleOf={(row) => row.transport.end_to_sealed.n}
                />
              )}
            </>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <SectionHeading
              id="latency"
              title="Transport latency"
              help="How long until audio is safe, in two steps: the phone telling us (long means a sleeping phone), then the upload."
            />
            <ToggleButtonGroup
              value={percentile}
              exclusive
              size="small"
              sx={{ mb: 1.5 }}
              onChange={(event, next) => next && setPercentile(next)}
            >
              {PERCENTILES.map(({ key, label }) => (
                <ToggleButton key={key} value={key}>
                  {label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
          <Legend
            items={hopSeries.map((hop) => ({
              ...hop,
              count: hopTotals[hop.key] || 0,
            }))}
            focused={focusedIn('hops')}
            onFocus={focusOn('hops')}
            onHover={hoverIn('hops')}
          />
          <LagChart
            days={days}
            series={visible('hops', hopSeries)}
            highlighted={highlightIn('hops')}
            percentile={percentile}
            sampleOf={(row) => row.transport.end_to_sealed.n}
          />
        </Paper>

        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
          <SectionHeading
            id="moment-to-push"
            title="Moment → push"
            help="From the end of a moment to the push about it. Everything above is part of that time."
          />
          {productDays.length ? (
            <>
              <Legend
                items={productSeries.map((lag) => ({
                  ...lag,
                  count: productTotals[lag.key] || 0,
                }))}
                focused={focusedIn('product')}
                onFocus={focusOn('product')}
                onHover={hoverIn('product')}
              />
              <LagChart
                days={productDays}
                series={visible('product', productSeries)}
                highlighted={highlightIn('product')}
                percentile={percentile}
                sampleOf={(row) => row.counts.surfaced}
                sampleNoun="moments surfaced"
              />
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 1 }}
              >
                Over moments the pipeline made, per UTC day surfaced — never a
                count of moments it missed. Moment → push and push → fetched
                stay empty until step 4b sends the push; until then moment →
                surfaced is the nearest number. The quotes themselves are on the
                Moments page.
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {payload?.product
                ? 'No moments surfaced in this window.'
                : 'Arrives with step 4a. Transport is one term in this number, not the number.'}
            </Typography>
          )}
        </Paper>

        <Divider sx={{ my: 4 }} />

        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
          <SectionHeading
            id="capture"
            title="Capture health"
            help="How well phones are recording. The rest of the page sees only audio that arrived, so a phone missing 15 minutes an hour looks fine there."
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Tile
              label="Accounts streaming"
              value={fmtCount(selectedCapture?.users)}
              help={CAPTURE_TILES.accounts}
            />
            <Tile
              label={`Accounts streaming · ${windowDays}d`}
              value={`${capture?.truncated ? '≥ ' : ''}${fmtCount(
                capture?.window_users
              )}`}
              help={`${CAPTURE_TILES.window_accounts}${
                capture?.truncated ? ' A floor: the oldest days were cut.' : ''
              }`}
            />
            <Tile
              label="Coverage p50"
              value={fmtCoverage(selectedCapture?.coverage.p50)}
              tone={coverageTone(
                selectedCapture?.coverage.p50,
                selectedCapture?.coverage.bar
              )}
              help={CAPTURE_TILES.coverage}
            />
            <Tile
              label="Accounts below bar"
              value={
                selectedCapture
                  ? `${fmtCount(
                      selectedCapture.coverage.users_below_bar
                    )} / ${fmtCount(selectedCapture.users)}`
                  : '—'
              }
              tone={
                selectedCapture?.coverage.users_below_bar > 0
                  ? 'error.main'
                  : undefined
              }
              help={CAPTURE_TILES.below_bar}
            />
            <Tile
              label="Audio held"
              value={`${fmtHours(captureTotals.audio_hours)} / ${fmtHours(
                captureTotals.declared_hours
              )}`}
              tone={
                captureTotals.declared_hours > captureTotals.audio_hours
                  ? 'warning.main'
                  : undefined
              }
              help={CAPTURE_TILES.audio_hours}
            />
            <Tile
              label="Audio missing"
              value={fmtHours(captureTotals.gap_hours)}
              tone={captureTotals.gap_hours > 0 ? 'error.main' : undefined}
              help={CAPTURE_TILES.gap_hours}
            />
            <Tile
              label="Bitrate p50"
              value={fmtKbps(selectedCapture?.kbps.p50)}
              tone={kbpsTone(
                selectedCapture?.kbps.p50,
                selectedCapture?.kbps.expected
              )}
              help={`${CAPTURE_TILES.kbps} Expected ${
                selectedCapture?.kbps.expected ?? '—'
              }k.`}
            />
            <Tile
              label="Off-cadence drains"
              value={fmtRate(selectedCapture?.rotation.short_segment_rate)}
              help={`${CAPTURE_TILES.short_rate} Expected ${
                selectedCapture?.rotation.expected_segment_s ?? '—'
              }s.`}
            />
            {capture && !capture.fleet && !user && (
              <Tile
                label="Recording, not declaring"
                value="n/a"
                help="Can't be narrowed by seal cause or build: those describe spans, and this counts phones."
              />
            )}
            {capture?.fleet && (
              <Tile
                label="Recording, not declaring"
                value={`${fmtCount(capture.fleet.silent)} / ${fmtCount(
                  capture.fleet.recording_now
                )}`}
                tone={capture.fleet.silent > 0 ? 'error.main' : undefined}
                help={`${CAPTURE_TILES.silent} After ${
                  capture.fleet.threshold_minutes
                } min, of ${fmtCount(capture.fleet.enrolled)} enrolled${
                  capture.fleet.truncated ? ' (a partial count)' : ''
                }.`}
              />
            )}
          </Box>

          <Legend
            items={[
              ...COVERAGE,
              ...CONTINUITY.map((entry) => ({
                ...entry,
                key: `continuity_${entry.key}`,
              })),
            ]}
            focused={focusedIn('coverage')}
            onFocus={focusOn('coverage')}
            onHover={hoverIn('coverage')}
          />
          <LagChart
            days={captureDays}
            series={visible('coverage', coverageSeries)}
            highlighted={highlightIn('coverage')}
            percentile={null}
            format={fmtCoverage}
            scale="value"
            sampleNoun="accounts declaring"
            sampleOf={(row) => row.users}
            height={220}
          />
          {capture?.user && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Last declare{' '}
                <b>
                  {capture.user.silent_for_s == null
                    ? 'never'
                    : `${fmtSeconds(capture.user.silent_for_s)} ago`}
                </b>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Last durable{' '}
                <b>
                  {capture.user.unsealed_for_s == null
                    ? 'never'
                    : `${fmtSeconds(capture.user.unsealed_for_s)} ago`}
                </b>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Microphone{' '}
                <b>
                  {capture.user.recording == null
                    ? 'never reported'
                    : capture.user.recording
                    ? 'live'
                    : 'off'}
                </b>
                {capture.user.last_stop_reason
                  ? ` · last stop ${capture.user.last_stop_reason}`
                  : ''}
              </Typography>
            </Box>
          )}
        </Paper>

        {pipeline?.transcription && (
          <TranscriptionHealth asr={pipeline.transcription} fleet={!user} />
        )}

        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
          <SectionHeading
            id="by-day"
            title="By day"
            help="Times follow the percentile toggle. Times count spans by the day they arrived; counts by the day they were declared."
          />
          <DayTable
            days={days}
            percentile={percentile}
            percentileLabel={percentileLabel}
            selectedDay={selectedDay}
            onSelectDay={setSelectedDay}
          />
        </Paper>
      </Refreshing>

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <SectionHeading
          id="spans-coming-in"
          title="Spans coming in"
          help="Everyone's spans as the router finishes them, newest first, with no names. One cell per minute: green was transcribed, grey kept. A bar under a minute is a quote found there. The filters above don't apply."
        />
        <FleetSpans />
      </Paper>

      {/* Everything above is the fleet's and names no account. These two are
          one phone's own rows, and the phone is the signed-in staff member's
          until the filter names someone else — the server resolves `me`.
          Kept apart so the fleet panels never read as though they had a
          user in them. */}
      <Divider sx={{ my: 6, borderBottomWidth: 3 }} />
      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
        One account
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {user
          ? `${
              user.username || user.id
            }’s own spans and what the router heard in them.`
          : 'Your own spans and what the router heard in them — stream a day, come back, and read it minute by minute. Pick a user above to look at another account instead.'}
      </Typography>

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <SectionHeading
          id="router-heard"
          title="What the router heard"
          help="This account's spans, newest first. One cell per minute: green was transcribed, grey kept. Open a span to see what was heard, read the transcript and play the quotes. Your own audio only (anyone's on develop)."
        />
        <RouterHeard user={user} focus={focusSpan} onClearFocus={clearFocus} />
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <SectionHeading
          id="spans"
          title="Spans"
          help="Every span this account declared, newest first. The gap between the phone's clock and ours is how long the audio sat on the phone."
        />
        <SpanTable user={user} coverageBar={selectedCapture?.coverage.bar} />
      </Paper>
    </Box>
  )
}

export default StreamIngest
